import React, { useEffect, useMemo, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useParams } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import {
  FaBoxOpen,
  FaChevronRight,
  FaCheckCircle,
  FaShoppingCart,
  FaEnvelope,
  FaCloud,
  FaMobileAlt,
  FaWhatsapp,
  FaServer,
  FaSpinner,
} from "react-icons/fa";

import "./Products.css";
import { BASE_URL } from "../../Config/config";

const API_URL = `${BASE_URL}/products.php`;
const IMAGE_URL = `${BASE_URL}/`;
const DEFAULT_PRODUCT_ICON = "FaBoxOpen";

const DynamicFaIcon = ({ name, ...props }) => {
  const IconComponent =
    FaIcons[name] || FaIcons[DEFAULT_PRODUCT_ICON] || FaIcons.FaBoxOpen;

  return <IconComponent {...props} />;
};
const normalizePrice = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const price = String(value).trim();

  if (price === "") {
    return null;
  }

  return price;
};
const isNumericPrice = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  const price = String(value).trim();

  if (price === "") {
    return false;
  }

  return /^\d+(?:\.\d+)?$/.test(price);
};

const formatPrice = (value) => {
  if (value === null || value === undefined) {
    return "Price on request";
  }

  const price = String(value).trim();

  if (price === "") {
    return "Price on request";
  }

  // Numeric DB price
  if (/^\d+(?:\.\d+)?$/.test(price)) {
    const numericPrice = Number(price);

    if (!Number.isFinite(numericPrice)) {
      return price;
    }

    return `₹${numericPrice.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // Text DB price — show exactly as stored
  return price;
};
const getGstAmount = (price, gst) => {
  if (price === null || price === undefined) {
    return null;
  }

  const priceText = String(price).trim();

  // Text price = NEVER calculate GST
  if (priceText === "") {
    return null;
  }

  // Only calculate GST if price is a pure number
  if (!/^\d+(?:\.\d+)?$/.test(priceText)) {
    return null;
  }

  const gstText = String(gst ?? "").trim();

  // GST must also be numeric
  if (gstText === "" || !/^\d+(?:\.\d+)?$/.test(gstText)) {
    return null;
  }

  const numericPrice = Number(priceText);
  const numericGst = Number(gstText);

  if (
    !Number.isFinite(numericPrice) ||
    !Number.isFinite(numericGst) ||
    numericGst <= 0
  ) {
    return null;
  }

  return ((numericPrice * numericGst) / 100).toFixed(2);
};
export default function Products() {
  const { product, category } = useParams();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [usersWiseSelections, setUsersWiseSelections] = useState({});
  const [activeProduct, setActiveProduct] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [selectedDescription, setSelectedDescription] = useState(null);
  const [usersWiseCategory, setUsersWiseCategory] = useState("new");
  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://") ||
      image.startsWith("blob:")
    ) {
      return image;
    }

    return `${IMAGE_URL}${image}`;
  };

  const productMap = {
    "tally-prime": "Tally Prime",
    miracle: "Miracle",
    busy: "BUSY",
    "odoo-erp": "Odoo ERP",
    "ottu-hrms": "Ottu HRMS",
  };

  const categoryMap = {
    "new-license": "New License",
    renewal: "Renewal",
    upgrade: "Upgrade",
    "tallyprime-server": "TallyPrime Server",
    "tally-on-mobile": "Tally on Mobile",
    "tally-on-cloud": "Tally on Cloud",
    "tally-on-whatsapp": "Tally on WhatsApp",
    amc: "AMC",
    "cloud-solutions": "Cloud Solutions",
    cloud: "Cloud",
    mobile: "Mobile",
    implementation: "Implementation",
    enterprise: "Enterprise",
    customization: "Customization",
    support: "Support",
    payroll: "Payroll",
    attendance: "Attendance",
    hrms: "HRMS",
  };
  const formatPrice = (value) => {
    const price = String(value ?? "").trim();

    if (price === "" || price.toLowerCase() === "price on request") {
      return "Price on request";
    }

    if (price === "0" || price === "0.0" || price === "0.00") {
      return "Price on request";
    }

    // Numeric price
    if (/^\d+(?:\.\d+)?$/.test(price)) {
      const numericPrice = Number(price);

      if (!Number.isFinite(numericPrice)) {
        return "Price on request";
      }

      return `₹${numericPrice.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    // Any text such as:
    // Contact Us
    // Price on Request
    // Starting from ₹500
    // Call for pricing
    return price;
  };
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API_URL}?action=get_products`);

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const data = await response.json();

      if (!data.status) {
        throw new Error(data.message || "Failed to fetch products.");
      }

      const apiProducts = Array.isArray(data.data) ? data.data : [];

      const activeProducts = apiProducts
        .filter((item) => Boolean(Number(item.status)))
        .map((item) => ({
          ...item,
          name: item.name || "",
          description: item.description || "",
          icon: item.icon || DEFAULT_PRODUCT_ICON,

          modules: Array.isArray(item.modules)
            ? item.modules.map((module) => ({
                ...module,

                name: module.name || "",

                description: module.description || "",

                icon: module.icon || "FaLayerGroup",

                subcategories: Array.isArray(module.subcategories)
                  ? module.subcategories.map((plan) => ({
                      ...plan,

                      id: Number(plan.id),

                      name: plan.name || "",

                      description: plan.description || "",

                      pricing_type: String(
                        plan.pricing_type || "fixed",
                      ).toLowerCase(),

                      price: normalizePrice(plan.price),

                      gst:
                        plan.gst !== null && plan.gst !== undefined
                          ? String(plan.gst).trim()
                          : "",

                      prices: Array.isArray(plan.prices)
                        ? plan.prices.map((price) => ({
                            ...price,

                            id: Number(price.id),

                            module_id: Number(price.module_id),

                            pricing_type: String(
                              price.pricing_type || "",
                            ).toLowerCase(),

                            price_category: price.price_category || "",

                            price: normalizePrice(price.price),
                          }))
                        : [],
                    }))
                  : [],
              }))
            : [],
        }));

      setProducts(activeProducts);

      if (activeProducts.length > 0) {
        let selectedProductName = product ? productMap[product] || product : "";

        let selectedProduct = activeProducts.find(
          (item) =>
            item.name.toLowerCase() ===
            String(selectedProductName).toLowerCase(),
        );

        if (!selectedProduct) {
          selectedProduct = activeProducts[0];
        }

        setActiveProduct(selectedProduct.name);

        const modules = selectedProduct.modules || [];

        if (modules.length > 0) {
          let selectedCategoryName = category
            ? categoryMap[category] || category
            : "";

          let selectedModule = modules.find(
            (item) =>
              item.name.toLowerCase() ===
              String(selectedCategoryName).toLowerCase(),
          );

          if (!selectedModule) {
            selectedModule = modules[0];
          }

          setActiveCategory(selectedModule?.name || "");
        } else {
          setActiveCategory("");
        }
      } else {
        setActiveProduct("");
        setActiveCategory("");
      }
    } catch (err) {
      console.error("FETCH PRODUCTS ERROR:", err);
      setError(err.message || "Unable to load products.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!products.length) {
      return;
    }

    const selectedProductName = product ? productMap[product] || product : "";

    let selectedProduct = products.find(
      (item) =>
        item.name.toLowerCase() === String(selectedProductName).toLowerCase(),
    );

    if (!selectedProduct) {
      selectedProduct = products[0];
    }

    setActiveProduct(selectedProduct?.name || "");

    const modules = selectedProduct?.modules || [];

    if (modules.length === 0) {
      setActiveCategory("");
      return;
    }

    const selectedCategoryName = category
      ? categoryMap[category] || category
      : "";

    let selectedModule = modules.find(
      (module) =>
        module.name.toLowerCase() ===
        String(selectedCategoryName).toLowerCase(),
    );

    if (!selectedModule) {
      selectedModule = modules[0];
    }

    setActiveCategory(selectedModule?.name || "");
  }, [product, category, products]);

  const currentProduct = useMemo(() => {
    return products.find((item) => item.name === activeProduct) || null;
  }, [products, activeProduct]);

  const currentModule = useMemo(() => {
    if (!currentProduct) {
      return null;
    }

    return (
      currentProduct.modules?.find(
        (module) => module.name === activeCategory,
      ) || null
    );
  }, [currentProduct, activeCategory]);

  const getYearlyPrice = (plan, priceCategory) => {
    if (!plan || !Array.isArray(plan.prices)) {
      return null;
    }

    const priceRow = plan.prices.find(
      (item) =>
        String(item?.pricing_type || "").toLowerCase() === "yearly" &&
        String(item?.price_category || "").toLowerCase() ===
          String(priceCategory || "").toLowerCase() &&
        item.price !== null &&
        item.price !== undefined &&
        String(item.price).trim() !== "",
    );

    return priceRow ? priceRow.price : null;
  };
  const getFixedPrice = (plan) => {
    if (!plan) {
      return null;
    }

    if (Array.isArray(plan.prices)) {
      const fixedRow = plan.prices.find(
        (item) =>
          String(item?.pricing_type || "").toLowerCase() === "fixed" &&
          item.price !== null &&
          item.price !== undefined &&
          String(item.price).trim() !== "",
      );

      if (fixedRow) {
        return fixedRow.price;
      }
    }

    return plan.price;
  };

  const getGstAmount = (price, gst) => {
    const numericPrice = Number(String(price ?? "").trim());

    const numericGst = Number(String(gst ?? "").trim());

    if (
      !Number.isFinite(numericPrice) ||
      !Number.isFinite(numericGst) ||
      numericGst <= 0
    ) {
      return null;
    }

    return ((numericPrice * numericGst) / 100).toFixed(0);
  };
  const getUsersWisePrices = (plan, category = "") => {
    if (!plan || !Array.isArray(plan.prices)) {
      return [];
    }

    let prices = plan.prices.filter(
      (item) =>
        item.pricing_type === "users_wise" &&
        item.price !== null &&
        item.price !== undefined &&
        item.price !== "",
    );

    if (category) {
      prices = prices.filter((item) => item.price_category === category);
    }

    return prices;
  };

  const getPlanSelectionKey = (plan, index) => {
    return String(plan.id || `plan-${index}`);
  };

  const getSelectedUsersWisePrice = (
    plan,
    index,
    selectedCategory = usersWiseCategory,
  ) => {
    const allPrices = getUsersWisePrices(plan, selectedCategory);

    if (!allPrices.length) {
      return null;
    }

    const key = getPlanSelectionKey(plan, index);
    const selection = usersWiseSelections[key];

    const category =
      selection?.category ||
      (allPrices.some((item) => item.price_category === "new")
        ? "new"
        : allPrices[0]?.price_category);

    const categoryPrices = allPrices;

    if (!categoryPrices.length) {
      return allPrices[0];
    }

    const selectedId = selection?.priceId;

    if (selectedId !== undefined && selectedId !== null && selectedId !== "") {
      const selected = categoryPrices.find(
        (item, priceIndex) =>
          String(item.id ?? priceIndex) === String(selectedId),
      );

      if (selected) {
        return selected;
      }
    }

    return categoryPrices[0];
  };

  const allCurrentPlans = currentModule?.subcategories || [];

  const hasYearlyPlans = useMemo(() => {
    return allCurrentPlans.some((plan) => {
      if (plan.pricing_type !== "yearly") {
        return false;
      }

      return (
        getYearlyPrice(plan, "1_year") !== null ||
        getYearlyPrice(plan, "2_years") !== null
      );
    });
  }, [allCurrentPlans]);

  const currentPlans = useMemo(() => {
    if (!currentModule) {
      return [];
    }

    if (yearFilter === "all") {
      return allCurrentPlans;
    }

    return allCurrentPlans.filter((plan) => {
      if (plan.pricing_type !== "yearly") {
        return false;
      }

      return getYearlyPrice(plan, yearFilter) !== null;
    });
  }, [currentModule, allCurrentPlans, yearFilter]);

  const changeCategory = (module) => {
    setActiveCategory(module.name);
    setYearFilter("all");
    setUsersWiseSelections({});
  };

  const changeProduct = (selectedProduct) => {
    setActiveProduct(selectedProduct.name);

    const firstModule = selectedProduct.modules?.[0];

    setActiveCategory(firstModule?.name || "");
    setYearFilter("all");
    setUsersWiseSelections({});
  };

  const getContactLink = (plan, selectedPrice = null) => {
    const productName = activeProduct || "";
    const moduleName = activeCategory || "";
    const planName = plan.name || "";

    let price = "Price on request";

    if (
      selectedPrice?.price !== undefined &&
      selectedPrice?.price !== null &&
      selectedPrice?.price !== ""
    ) {
      price = selectedPrice.price;
    } else if (plan.pricing_type === "yearly" && yearFilter !== "all") {
      const yearlyPrice = getYearlyPrice(plan, yearFilter);

      if (yearlyPrice !== null) {
        price = yearlyPrice;
      }
    } else if (plan.pricing_type === "fixed") {
      const fixedPrice = getFixedPrice(plan);

      if (fixedPrice !== null) {
        price = fixedPrice;
      }
    }

    /*
     * -------------------------------------------------------
     * SELECTED USER VALUE
     * -------------------------------------------------------
     */

    let selectedValue = "";

    if (plan.pricing_type === "users_wise" && selectedPrice) {
      selectedValue =
        selectedPrice.plan_name || `${selectedPrice.user_count || ""} User`;
    }

    const selectedModule = `${productName} - ${moduleName} - ${planName}`;

    const automaticMessage =
      `Hello, I am interested in ${selectedModule}. ` +
      (selectedValue ? `Selected User Plan: ${selectedValue}. ` : "") +
      `Please provide more details, pricing and available options.`;

    const params = new URLSearchParams({
      product: productName,
      module: moduleName,
      title: selectedModule,
      code: plan.code || planName,
      price: price,
      plan: planName,
      selectedValue: selectedValue,
      message: automaticMessage,
    });

    return `/contact?${params.toString()}`;
  };

  const getPlanIcon = (plan) => {
    const categoryName = String(activeCategory || "").toLowerCase();
    const planName = String(plan?.name || "").toLowerCase();

    if (categoryName.includes("cloud") || planName.includes("cloud")) {
      return <FaCloud />;
    }

    if (categoryName.includes("mobile") || planName.includes("mobile")) {
      return <FaMobileAlt />;
    }

    if (categoryName.includes("whatsapp") || planName.includes("whatsapp")) {
      return <FaWhatsapp />;
    }

    if (categoryName.includes("server") || planName.includes("server")) {
      return <FaServer />;
    }

    return <FaCheckCircle />;
  };

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out-cubic",
      once: false,
      offset: 80,
    });
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [activeProduct, activeCategory, currentPlans.length, yearFilter]);
  function ProductDescription({ plan, onViewMore }) {
    const descriptionRef = React.useRef(null);
    const [isLong, setIsLong] = useState(false);

    useEffect(() => {
      const element = descriptionRef.current;

      if (!element) return;

      const checkDescription = () => {
        setIsLong(element.scrollHeight > element.clientHeight + 1);
      };

      const timer = setTimeout(checkDescription, 100);

      checkDescription();

      window.addEventListener("resize", checkDescription);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", checkDescription);
      };
    }, [plan.description]);

    const handleViewMore = (e) => {
      e.preventDefault();
      e.stopPropagation();

      onViewMore({
        name: plan.name,
        description: plan.description,
      });
    };

    return (
      <div className="sc-modules-description-wrapper">
        <p ref={descriptionRef} className="sc-modules-description">
          {plan.description}
        </p>

        {isLong && (
          <button
            type="button"
            className="sc-modules-view-more"
            onClick={handleViewMore}
          >
            View More
          </button>
        )}
      </div>
    );
  }
  if (loading) {
    return (
      <main className="products-page">
        <section className="products-loading">
          <FaSpinner className="products-loading-spinner" />
          <h3>Loading Products...</h3>
          <p>Please wait while we load our products and solutions.</p>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="products-page">
        <section className="products-error">
          <FaBoxOpen />
          <h2>Unable to Load Products</h2>
          <p>{error}</p>

          <button
            type="button"
            onClick={fetchProducts}
            className="retry-products-btn"
          >
            Try Again
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="products-page">
      <section className="products-main">
        <div className="section-container">
          <div className="products-layout">
            <aside
              className="products-sidebar"
              data-aos="fade-right"
              data-aos-duration="900"
            >
              <div className="sidebar-title">
                <span>OUR PRODUCTS</span>
                <h3>Choose Solution</h3>
              </div>

              <div className="product-menu">
                {products.length === 0 ? (
                  <div className="no-products-sidebar">
                    No products available.
                  </div>
                ) : (
                  products.map((item, index) => (
                    <button
                      type="button"
                      key={item.id}
                      data-aos="fade-right"
                      data-aos-delay={100 + index * 100}
                      className={`product-menu-item ${
                        activeProduct === item.name ? "active" : ""
                      }`}
                      onClick={() => changeProduct(item)}
                    >
                      <div className="product-menu-icon">
                        <DynamicFaIcon name={item.icon} />
                      </div>

                      <div className="product-menu-text">
                        <strong>{item.name}</strong>

                        <small>
                          {item.description || "Business software solution"}
                        </small>
                      </div>

                      <FaChevronRight className="product-menu-arrow" />
                    </button>
                  ))
                )}
              </div>
            </aside>

            <div className="products-content">
              {!currentProduct ? (
                <div className="products-empty">
                  <FaBoxOpen />
                  <h3>No Product Selected</h3>
                  <p>Please select a product from the list.</p>
                </div>
              ) : (
                <>
                  {currentProduct.modules?.length > 0 && (
                    <div
                      className="subcategory-section"
                      data-aos="fade-up"
                      data-aos-delay="150"
                    >
                      <div className="subcategory-title">
                        <span>SUB PRODUCTS</span>
                        <h3>Choose a Category</h3>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "20px",
                        }}
                      >
                        <div className="subcategory-tabs">
                          {currentProduct.modules.map((module, index) => (
                            <button
                              type="button"
                              key={module.id}
                              data-aos="zoom-in"
                              data-aos-delay={100 + index * 80}
                              className={
                                activeCategory === module.name ? "active" : ""
                              }
                              onClick={() => changeCategory(module)}
                            >
                              <DynamicFaIcon name={module.icon} />
                              <span>{module.name}</span>
                            </button>
                          ))}
                        </div>

                        <div className="subcategory-title1">
                          <span>
                            {currentPlans.length}{" "}
                            {currentPlans.length === 1 ? "Plan" : "Plans"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    className={`plan-row1 ${
                      (currentModule &&
                      allCurrentPlans.some(
                        (plan) =>
                          plan.pricing_type === "users_wise" &&
                          getUsersWisePrices(plan).length > 0,
                      )
                        ? 1
                        : 0) +
                        (currentModule &&
                        allCurrentPlans.length > 0 &&
                        hasYearlyPlans
                          ? 1
                          : 0) ===
                      1
                        ? "single-filter"
                        : "two-filters"
                    }`}
                  >
                    {currentModule &&
                      allCurrentPlans.some(
                        (plan) =>
                          plan.pricing_type === "users_wise" &&
                          getUsersWisePrices(plan).length > 0,
                      ) && (
                        <div className="plans-filter-wrapper">
                          <div className="yearly-filter">
                            <button
                              type="button"
                              className={
                                usersWiseCategory === "new"
                                  ? "yearly-filter-btn active"
                                  : "yearly-filter-btn"
                              }
                              onClick={() => setUsersWiseCategory("new")}
                            >
                              New
                            </button>

                            <button
                              type="button"
                              className={
                                usersWiseCategory === "renewal"
                                  ? "yearly-filter-btn active"
                                  : "yearly-filter-btn"
                              }
                              onClick={() => setUsersWiseCategory("renewal")}
                            >
                              Renewal
                            </button>
                          </div>
                        </div>
                      )}
                    {currentModule &&
                      allCurrentPlans.length > 0 &&
                      hasYearlyPlans && (
                        <div className="plans-filter-wrapper">
                          <div className="yearly-filter">
                            <button
                              type="button"
                              className={
                                yearFilter === "all"
                                  ? "yearly-filter-btn active"
                                  : "yearly-filter-btn"
                              }
                              onClick={() => setYearFilter("all")}
                            >
                              All
                            </button>

                            <button
                              type="button"
                              className={
                                yearFilter === "1_year"
                                  ? "yearly-filter-btn active"
                                  : "yearly-filter-btn"
                              }
                              onClick={() => setYearFilter("1_year")}
                            >
                              1 Year
                            </button>

                            <button
                              type="button"
                              className={
                                yearFilter === "2_years"
                                  ? "yearly-filter-btn active"
                                  : "yearly-filter-btn"
                              }
                              onClick={() => setYearFilter("2_years")}
                            >
                              2 Years
                            </button>
                          </div>
                        </div>
                      )}

                    <div></div>
                  </div>

                  {currentPlans.length === 0 ? (
                    <div className="plans-empty">
                      <FaBoxOpen />

                      <h3>No plans available</h3>

                      <p>
                        No subcategories have been added under this category
                        yet.
                      </p>

                      <a href="/contact" className="empty-contact-btn">
                        Enquire Now
                      </a>
                    </div>
                  ) : (
                    <div className="plans-grid">
                      {currentPlans.map((plan, index) => {
                        let selectedUsersWisePrice = null;

                        if (plan.pricing_type === "users_wise") {
                          selectedUsersWisePrice = getSelectedUsersWisePrice(
                            plan,
                            index,
                          );
                        }

                        return (
                          <div
                            className="plan-card"
                            key={plan.id || index}
                            data-aos="fade-up"
                            data-aos-delay={100 + index * 100}
                            data-aos-duration="800"
                          >
                            <div className="plan-image-wrapper">
                              {plan.image ? (
                                <img
                                  src={getImageUrl(plan.image)}
                                  alt={plan.name}
                                  className="plan-image"
                                />
                              ) : (
                                <div className="plan-no-image">
                                  {getPlanIcon(plan)}
                                </div>
                              )}
                            </div>

                            <div className="plan-card-top">
                              <div className="plan-number">
                                {String(index + 1).padStart(2, "0")}
                              </div>

                              <div className="plan-icon">
                                {getPlanIcon(plan)}
                              </div>
                            </div>

                            <h3>{plan.name}</h3>

                            {plan.description && (
                              <ProductDescription
                                plan={plan}
                                onViewMore={setSelectedDescription}
                              />
                            )}

                            {plan.pricing_type === "users_wise"
                              ? (() => {
                                  const usersWisePrices = getUsersWisePrices(
                                    plan,
                                    usersWiseCategory,
                                  );

                                  const selectionKey = getPlanSelectionKey(
                                    plan,
                                    index,
                                  );

                                  const selectedPriceId =
                                    usersWiseSelections[selectionKey]?.priceId;

                                  const selectedPrice =
                                    usersWisePrices.find(
                                      (item, priceIndex) =>
                                        String(item.id ?? priceIndex) ===
                                        String(selectedPriceId),
                                    ) || usersWisePrices[0];

                                  return (
                                    <div className="users-wise-card-pricing">
                                      {usersWisePrices.length > 0 ? (
                                        <div
                                          className="users-wise-select-wrapper"
                                          style={{ marginTop: "5px" }}
                                        >
                                          <label>Select User Plan</label>

                                          <select
                                            value={
                                              selectedPrice
                                                ? String(
                                                    selectedPrice.id ??
                                                      usersWisePrices.indexOf(
                                                        selectedPrice,
                                                      ),
                                                  )
                                                : ""
                                            }
                                            onChange={(e) => {
                                              setUsersWiseSelections(
                                                (prev) => ({
                                                  ...prev,
                                                  [selectionKey]: {
                                                    priceId: e.target.value,
                                                  },
                                                }),
                                              );
                                            }}
                                          >
                                            {usersWisePrices.map(
                                              (price, priceIndex) => (
                                                <option
                                                  key={
                                                    price.id ||
                                                    `${selectionKey}-${priceIndex}`
                                                  }
                                                  value={String(
                                                    price.id ?? priceIndex,
                                                  )}
                                                >
                                                  {price.plan_name}
                                                </option>
                                              ),
                                            )}
                                          </select>
                                        </div>
                                      ) : (
                                        <div
                                          className="yearly-price-row"
                                          style={{ marginTop: "5px" }}
                                        >
                                          <div className="plan-price">
                                            Price on request
                                          </div>
                                        </div>
                                      )}
                                      {selectedPrice &&
                                        isNumericPrice(selectedPrice.price) &&
                                        getGstAmount(
                                          selectedPrice.price,
                                          plan.gst,
                                        ) !== null && (
                                          <div className="plan-gst">
                                            +{plan.gst}% GST (₹
                                            {getGstAmount(
                                              selectedPrice.price,
                                              plan.gst,
                                            )}
                                            )
                                          </div>
                                        )}
                                    </div>
                                  );
                                })()
                              : plan.pricing_type === "yearly"
                                ? (() => {
                                    const oneYearPrice = getYearlyPrice(
                                      plan,
                                      "1_year",
                                    );

                                    const twoYearPrice = getYearlyPrice(
                                      plan,
                                      "2_years",
                                    );

                                    const renderGst = (price) => {
                                      const gstAmount = getGstAmount(
                                        price,
                                        plan.gst,
                                      );

                                      if (gstAmount === null) {
                                        return null;
                                      }

                                      return (
                                        <small className="yearly-gst">
                                          +{plan.gst}% GST (₹{gstAmount})
                                        </small>
                                      );
                                    };

                                    return (
                                      <div className="yearly-card-pricing">
                                        {yearFilter === "all" ? (
                                          <div className="yearly-price-list">
                                            {oneYearPrice !== null && (
                                              <div className="yearly-price-row">
                                                <strong>
                                                  {formatPrice(oneYearPrice)}
                                                </strong>

                                                <div>
                                                  <span>1 Year</span>

                                                  {renderGst(oneYearPrice)}
                                                </div>
                                              </div>
                                            )}

                                            {twoYearPrice !== null && (
                                              <div className="yearly-price-row">
                                                <strong>
                                                  {formatPrice(twoYearPrice)}
                                                </strong>

                                                <div>
                                                  <span>2 Years</span>

                                                  {renderGst(twoYearPrice)}
                                                </div>
                                              </div>
                                            )}

                                            {oneYearPrice === null &&
                                              twoYearPrice === null && (
                                                <div className="yearly-price-row">
                                                  <div className="plan-price">
                                                    Price on request
                                                  </div>
                                                </div>
                                              )}
                                          </div>
                                        ) : (
                                          (() => {
                                            const selectedYearPrice =
                                              yearFilter === "1_year"
                                                ? oneYearPrice
                                                : twoYearPrice;

                                            const selectedYearLabel =
                                              yearFilter === "1_year"
                                                ? "1 Year"
                                                : "2 Years";

                                            return (
                                              <div className="yearly-selected-wrapper">
                                                <div className="yearly-price-row">
                                                  <strong>
                                                    {formatPrice(
                                                      selectedYearPrice,
                                                    )}
                                                  </strong>

                                                  <div>
                                                    <span>
                                                      {selectedYearLabel}
                                                    </span>

                                                    {renderGst(
                                                      selectedYearPrice,
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })()
                                        )}
                                      </div>
                                    );
                                  })()
                                : (() => {
                                    const fixedPrice = getFixedPrice(plan);
                                    const gstAmount = getGstAmount(
                                      fixedPrice,
                                      plan.gst,
                                    );

                                    return (
                                      <div className="yearly-price-row">
                                        <div>
                                          <strong>
                                            {formatPrice(fixedPrice)}
                                          </strong>

                                          {gstAmount !== null && (
                                            <div className="yearly-selected-gst">
                                              +{plan.gst}% GST (₹{gstAmount})
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}
                            <div className="plan-divider"></div>

                            <div className="plan-actions">
                              <a
                                href={getContactLink(
                                  plan,
                                  selectedUsersWisePrice,
                                )}
                                className="buy-btn"
                              >
                                <FaShoppingCart />
                                Buy Now
                              </a>

                              <a href="/contact" className="enquire-btn">
                                <FaEnvelope />
                                Enquire
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      {selectedDescription && (
        <div
          className="description-modal-overlay"
          onClick={() => setSelectedDescription(null)}
        >
          <div
            className="description-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="description-modal-header">
              <h3>{selectedDescription.name}</h3>

              <button
                type="button"
                className="description-modal-close"
                onClick={() => setSelectedDescription(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="description-modal-body">
              <p>{selectedDescription.description}</p>
            </div>

            <div className="description-modal-footer">
              <button
                type="button"
                className="description-modal-ok"
                onClick={() => setSelectedDescription(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
